import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { playWaterDropSound } from "@/lib/notifySound";
import { Wallet, ShieldAlert, ShieldCheck, Loader2, Upload, HelpCircle, Megaphone, Share2, Mic, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import WorkGmailTemp from "@/components/WorkGmailTemp";
import DashboardTour from "@/components/DashboardTour";
import SupervisionBroadcaster from "@/components/SupervisionBroadcaster";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const TOUR_STEPS = [
  { targetTestId: "salary-card", title: "Your salary", text: "Your monthly salary and payout status live here." },
  { targetTestId: "payment-status-card", title: "Payment status", text: "\"Cleared\" means Form 2 is done and payout is unblocked. \"Blocked\" means it isn't yet." },
  { targetTestId: "tasks-section", title: "Your tasks", text: "Tasks your admin assigns show up here. Mark each Working or Finished as you go." },
  { targetTestId: "dept-section", title: "Department data", text: "Everything relevant to your section — applications, bookings, or tickets — appears here." },
  { targetTestId: "work-gmail-section", title: "Work Gmail", text: "Connect your section's work mailbox to read, compose and reply to email right from this dashboard." },
];

const DEPT_LABELS = { team: "Team", book: "Book", help: "Help", official: "Official" };
const ESCALATE_MAX_WORDS = 500;
const SHARE_MAX_BYTES = 30 * 1024 * 1024;
const SOCIAL_PLATFORMS = [
  { key: "instagram", label: "Instagram", Icon: Instagram, login: "https://www.instagram.com/accounts/login/", home: "https://www.instagram.com/" },
  { key: "facebook", label: "Facebook", Icon: Facebook, login: "https://www.facebook.com/login/", home: "https://www.facebook.com/" },
  { key: "youtube", label: "YouTube", Icon: Youtube, login: "https://accounts.google.com/ServiceLogin?service=youtube", home: "https://www.youtube.com/" },
  { key: "linkedin", label: "LinkedIn", Icon: Linkedin, login: "https://www.linkedin.com/login", home: "https://www.linkedin.com/" },
];

function EscalateDialog() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const wordCount = message.trim() ? message.trim().split(/\s+/).length : 0;
  const overLimit = wordCount > ESCALATE_MAX_WORDS;

  const send = async () => {
    if (!message.trim() || overLimit) return;
    setBusy(true);
    try {
      await api.post("/employee/escalate", { message: message.trim() });
      toast.success("Escalation sent to Counseltaria");
      setMessage(""); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button data-testid="escalate-open-btn" onClick={() => setOpen(true)}
        className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1.5"
        style={{ borderColor: "var(--accent)", color: "var(--accent-hover)" }}>
        <Megaphone className="w-3.5 h-3.5" /> Escalate
      </button>
      <DialogContent data-testid="escalate-dialog">
        <DialogHeader>
          <DialogTitle>Escalate to Counseltaria</DialogTitle>
        </DialogHeader>
        <Textarea data-testid="escalate-textarea" rows={6} value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Describe the issue you'd like to escalate (max 500 words)…" />
        <div className="text-xs" data-testid="escalate-word-count"
          style={{ color: overLimit ? "var(--accent)" : "var(--text-secondary)" }}>
          {wordCount}/{ESCALATE_MAX_WORDS} words{overLimit ? " — please shorten your message" : ""}
        </div>
        <DialogFooter>
          <button data-testid="escalate-send-btn" disabled={busy || !message.trim() || overLimit}
            onClick={send} className="btn-primary text-sm disabled:opacity-50">
            {busy ? "Sending…" : "Send"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShareDialog() {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > SHARE_MAX_BYTES) {
      toast.error("File is too large (max 30MB).");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const send = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employee/share", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.email_sent ? "File shared — Counseltaria has been notified" : "File shared, but the notification email failed to send");
      setFile(null); setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button data-testid="share-open-btn" onClick={() => setOpen(true)}
        className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1.5">
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share a file with Counseltaria</DialogTitle>
        </DialogHeader>
        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Any file up to 30MB. It's stored securely and a download link is emailed to the admin.</p>
        <input ref={fileRef} type="file" hidden onChange={pick} data-testid="share-file-input" />
        <button type="button" onClick={() => fileRef.current?.click()}
          className="btn-outline text-sm inline-flex items-center gap-2 w-fit" data-testid="share-file-pick-btn">
          <Upload className="w-4 h-4" /> {file ? file.name : "Choose file"}
        </button>
        <DialogFooter>
          <button data-testid="share-send-btn" disabled={busy || !file}
            onClick={send} className="btn-primary text-sm disabled:opacity-50">
            {busy ? "Uploading…" : "Send"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const VOICE_NOTE_MAX_MS = 10000;
const VOICE_NOTE_UNSEND_SECONDS = 30;

// Prefer mp4/AAC first: it plays back on every browser (old and new Safari,
// Chrome, Firefox) without issue. WebM/Opus is only a fallback for browsers
// that can't record mp4 (e.g. Firefox) — those browsers can still play
// webm back fine themselves, but an older Safari admin viewing a
// Firefox-recorded webm note would still hit "operation not supported".
// That's an accepted residual edge case; mp4-first fixes the common case.
function pickVoiceNoteMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/mp4;codecs=mp4a.40.2", "audio/mp4", "audio/webm;codecs=opus", "audio/webm"];
  return candidates.find(t => MediaRecorder.isTypeSupported?.(t)) || "";
}

function VoiceNoteButton() {
  const [recording, setRecording] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const mimeTypeRef = useRef("");
  const stopTimerRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => () => {
    clearTimeout(stopTimerRef.current);
    clearInterval(countdownRef.current);
    streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const sendRecording = async () => {
    const mimeType = mimeTypeRef.current || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mimeType });
    if (blob.size === 0) return;
    try {
      const ext = mimeType.includes("mp4") ? "m4a" : "webm";
      const fd = new FormData();
      fd.append("file", blob, `voice-note.${ext}`);
      const { data } = await api.post("/employee/voice-notes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Voice message sent");
      setPendingId(data.id);
      const expiresAt = Date.now() + VOICE_NOTE_UNSEND_SECONDS * 1000;
      setSecondsLeft(VOICE_NOTE_UNSEND_SECONDS);
      countdownRef.current = setInterval(() => {
        const left = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
        setSecondsLeft(left);
        if (left <= 0) { clearInterval(countdownRef.current); setPendingId(null); }
      }, 1000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startRecording = async (e) => {
    e.preventDefault();
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickVoiceNoteMimeType();
      mimeTypeRef.current = mimeType;
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = () => { streamRef.current?.getTracks().forEach(t => t.stop()); sendRecording(); };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      stopTimerRef.current = setTimeout(() => stopRecording(), VOICE_NOTE_MAX_MS);
    } catch (e2) {
      toast.error("Microphone access is needed to record a voice message.");
    }
  };

  const stopRecording = (e) => {
    if (e) e.preventDefault();
    clearTimeout(stopTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const unsend = async () => {
    if (!pendingId) return;
    try {
      await api.delete(`/employee/voice-notes/${pendingId}`);
      toast.success("Voice message unsent");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { clearInterval(countdownRef.current); setPendingId(null); }
  };

  return (
    <div className="inline-flex items-center gap-2">
      <button data-testid="voice-note-record-btn" type="button"
        onMouseDown={startRecording} onMouseUp={stopRecording} onMouseLeave={(e) => recording && stopRecording(e)}
        onTouchStart={startRecording} onTouchEnd={stopRecording} onContextMenu={(e) => e.preventDefault()}
        className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1.5 select-none"
        style={recording ? { background: "var(--accent)", color: "#fff", borderColor: "var(--accent)" } : undefined}>
        <Mic className="w-3.5 h-3.5" /> {recording ? "Recording… release to send" : "Say a sentence"}
      </button>
      {pendingId && (
        <button data-testid="voice-note-unsend-btn" type="button" onClick={unsend}
          className="text-xs underline" style={{ color: "var(--accent)" }}>
          Unsend ({secondsLeft}s)
        </button>
      )}
    </div>
  );
}

function SocialShortcuts({ connections, onToggle }) {
  return (
    <section className="mb-10" data-testid="social-shortcuts-section">
      <h2 className="font-serif-display text-2xl mb-4">Brand social accounts</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        {SOCIAL_PLATFORMS.map(({ key, label, Icon, login, home }) => {
          const connected = !!connections?.[key];
          return (
            <div key={key} data-testid={`social-card-${key}`} className="card-soft p-4 flex items-center justify-between gap-3">
              <button data-testid={`social-open-${key}`}
                onClick={() => window.open(connected ? home : login, "_blank", "noopener,noreferrer")}
                className="btn-outline text-sm inline-flex items-center gap-2">
                <Icon className="w-4 h-4" /> {label}
              </button>
              <label className="inline-flex items-center gap-2 text-xs cursor-pointer" style={{ color: "var(--text-secondary)" }}>
                Connected
                <Switch data-testid={`social-connected-toggle-${key}`} checked={connected}
                  onCheckedChange={(v) => onToggle(key, v)} />
              </label>
            </div>
          );
        })}
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--text-secondary)" }}>
        Log in once via the button above, then flip "Connected" — next time the button opens the account page directly.
      </p>
    </section>
  );
}

function Form2Card({ onDone }) {
  const [open, setOpen] = useState(true);
  const [f, setF] = useState({ aadhaar: "", pan: "", upi_id: "", photo_url: "", alternate_mobile: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employee/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", data.photo_url);
      toast.success("Photo uploaded");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/employee/form2", f);
      toast.success("Employee details submitted - payment unblocked");
      if (typeof onDone === "function") await onDone();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div data-testid="form2-card" className="card-soft p-6 mb-8">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-serif-display text-xl">Employee Details (Form 2)</div>
          <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
            Required before your salary payment can be cleared. You can postpone this for now.
          </div>
        </div>
        <button type="button" data-testid="form2-postpone" onClick={() => setOpen(false)}
          className="text-xs underline flex-shrink-0" style={{ color: "var(--text-secondary)" }}>
          Postpone
        </button>
      </div>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="label-eyebrow block mb-2">Aadhaar number</label>
          <input required data-testid="form2-aadhaar" maxLength={12} value={f.aadhaar} onChange={e => set("aadhaar", e.target.value)}
            placeholder="12-digit number"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">PAN number</label>
          <input required data-testid="form2-pan" maxLength={10} value={f.pan} onChange={e => set("pan", e.target.value.toUpperCase())}
            placeholder="ABCDE1234F"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">UPI ID</label>
          <input required data-testid="form2-upi" value={f.upi_id} onChange={e => set("upi_id", e.target.value)}
            placeholder="name@bank"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Alternate mobile</label>
          <input required data-testid="form2-alt-mobile" value={f.alternate_mobile} onChange={e => set("alternate_mobile", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-2">Address</label>
          <textarea required data-testid="form2-address" rows={3} value={f.address} onChange={e => set("address", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)] resize-none" />
        </div>
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-2">Photo</label>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadPhoto} data-testid="form2-photo-input" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="btn-outline text-sm inline-flex items-center gap-2" data-testid="form2-photo-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {f.photo_url ? "Photo uploaded" : "Upload photo"}
          </button>
        </div>
        {err && <div data-testid="form2-error" className="sm:col-span-2 text-sm" style={{ color: "var(--accent)" }}>{err}</div>}
        <div className="sm:col-span-2">
          <button data-testid="form2-submit" disabled={busy} className="btn-primary w-full">
            {busy ? "Submitting…" : "Submit Form 2"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function EmployeeDashboard() {
  const [data, setData] = useState(null);
  const [deptData, setDeptData] = useState(null);
  const [showTour, setShowTour] = useState(false);
  const seenTaskIdsRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: dash }, { data: dept }] = await Promise.all([
        api.get("/employee/dashboard"),
        api.get("/employee/department-data"),
      ]);
      setData(dash);
      setDeptData(dept);
      if (!dash.tour_completed) setShowTour(true);
      const currentIds = new Set((dash.tasks || []).map(t => t.id));
      if (seenTaskIdsRef.current === null) {
        seenTaskIdsRef.current = currentIds;
      } else {
        const freshTasks = (dash.tasks || []).filter(t => !seenTaskIdsRef.current.has(t.id));
        freshTasks.forEach(t => toast.info(`New task from admin: ${t.title}`));
        if (freshTasks.length) playWaterDropSound();
        seenTaskIdsRef.current = currentIds;
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
  }, []);

  const finishTour = async () => {
    setShowTour(false);
    if (!data?.tour_completed) {
      try {
        await api.post("/employee/tour/complete");
        setData(prev => (prev ? { ...prev, tour_completed: true } : prev));
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const toggleTask = async (task) => {
    const next = task.status === "working" ? "finished" : "working";
    try {
      await api.patch(`/employee/tasks/${task.id}`, { status: next });
      setData(prev => ({ ...prev, tasks: prev.tasks.map(t => t.id === task.id ? { ...t, status: next } : t) }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSocialConnected = async (platform, connected) => {
    try {
      await api.patch(`/employee/social/${platform}/connected`, { connected });
      setData(prev => ({ ...prev, social_connections: { ...prev.social_connections, [platform]: connected } }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="employee-dashboard">
      <SupervisionBroadcaster employee={data} />
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{DEPT_LABELS[data.department] || data.department} Employee</span>
        <button data-testid="replay-tour-btn" onClick={() => setShowTour(true)}
          className="text-xs underline inline-flex items-center gap-1.5" style={{ color: "var(--text-secondary)" }}>
          <HelpCircle className="w-3.5 h-3.5" /> Replay Dashboard Tour
        </button>
      </div>
      <h1 className="font-serif-display text-4xl mt-2 mb-8">Welcome, {data.name.split(" ")[0]}</h1>

      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        <div className="card-soft p-5 flex items-center gap-3" data-testid="salary-card">
          <Wallet className="w-6 h-6" style={{ color: "var(--brand)" }} />
          <div>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Monthly salary</div>
            <div className="text-lg font-medium">Rs. {data.salary?.toLocaleString("en-IN")}</div>
          </div>
        </div>
        <div className="card-soft p-5 flex items-center gap-3" data-testid="payment-status-card">
          {data.payment_status === "cleared"
            ? <ShieldCheck className="w-6 h-6" style={{ color: "var(--brand)" }} />
            : <ShieldAlert className="w-6 h-6" style={{ color: "var(--accent)" }} />}
          <div>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>Payment status</div>
            <div className="text-lg font-medium capitalize" data-testid="payment-status-value">{data.payment_status}</div>
          </div>
        </div>
      </div>

      {!data.form2_submitted && <Form2Card onDone={load} />}

      <WorkGmailTemp workEmail={data.email} />

      <section className="mb-10" data-testid="tasks-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <h2 className="font-serif-display text-2xl">Your tasks</h2>
          <div className="flex items-center gap-2">
            <EscalateDialog />
            <ShareDialog />
            <VoiceNoteButton />
          </div>
        </div>
        {data.tasks.length === 0 && (
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No tasks assigned yet.</p>
        )}
        <div className="space-y-3">
          {data.tasks.map(t => (
            <div key={t.id} data-testid={`employee-task-${t.id}`} className="card-soft p-4 flex items-center justify-between gap-4">
              <div>
                <div className="font-medium">{t.title}</div>
                {t.description && <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{t.description}</div>}
                {t.description_file_url && (
                  <a data-testid={`employee-task-file-${t.id}`} href={t.description_file_url} target="_blank" rel="noreferrer"
                    className="text-xs underline inline-block mt-1" style={{ color: "var(--brand)" }}>
                    guide file ↗
                  </a>
                )}
              </div>
              <label className="inline-flex items-center gap-2 text-xs" style={{ color: "var(--text-secondary)" }}>
                {t.status === "finished" ? "Finished" : "Working"}
                <Switch data-testid={`employee-task-toggle-${t.id}`} checked={t.status === "working"}
                  onCheckedChange={() => toggleTask(t)} />
              </label>
            </div>
          ))}
        </div>
      </section>

      {data.department === "official" && (
        <SocialShortcuts connections={data.social_connections} onToggle={toggleSocialConnected} />
      )}

      {deptData && (
        <section data-testid="dept-section">
          <h2 className="font-serif-display text-2xl mb-4">{deptData.label}</h2>
          {deptData.note && <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{deptData.note}</p>}
          {deptData.items.length === 0 && !deptData.note && (
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Nothing here yet.</p>
          )}
          <div className="space-y-2">
            {deptData.items.map(item => (
              <div key={item.id} data-testid={`dept-item-${item.id}`} className="card-soft p-3 text-sm flex items-center justify-between gap-3">
                <span>{item.name || item.subject || item.service_name || item.id}</span>
                <span className="text-xs capitalize" style={{ color: "var(--text-secondary)" }}>{item.status}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {showTour && <DashboardTour steps={TOUR_STEPS} onDone={finishTour} />}
    </div>
  );
}
